import foundationData from "./command-vocabulary-foundation.json";
import identityData from "./generated/command-vocabulary-identities.json";
import publishedSupplementData from "./command-vocabulary-published-supplement.json";
import releaseData from "./command-vocabulary-release.json";
import readablePronunciationsData from "./readable-pronunciations.json";
import { READINGS } from "./catalog";
import type { Reading, ReadingOutcome, WordInsight } from "./types";

export type CommandVocabularyHandle =
  | "think-precisely"
  | "speak-with-confidence"
  | "understand-people"
  | "write-memorably";

export type CommandVocabularyRegister =
  | "conversational"
  | "professional"
  | "formal"
  | "literary"
  | "rhetorical";

export type CommandVocabularyArticulation =
  | "direct"
  | "warm-authority"
  | "restrained"
  | "tactful"
  | "punchy";

export type CommandVocabularyEntry = {
  id: string;
  word: string;
  dictionaryIdentity: {
    lemma: string;
    sourceSenseId: string;
  };
  phonetic: string;
  pronunciationProvenance: {
    source: string;
    values: Array<{ value: string; variety?: string }>;
  } | null;
  partOfSpeech: string;
  meaning: string;
  alternateMeaning?: string;
  synonym: string;
  antonym: string;
  family: string;
  relatedFamilyMembers?: Array<{
    word: string;
    partOfSpeech: string;
    sourceSenseIds: string[];
    relationship: string;
    meaning: string;
    example: string;
  }>;
  whyWorthHaving: string;
  handles: CommandVocabularyHandle[];
  registers: CommandVocabularyRegister[];
  articulations: CommandVocabularyArticulation[];
  distinction?: { nearbyWord: string; explanation: string };
  combinations: string[];
  examples: Array<{ context: string; sentence: string }>;
  landingRange?: {
    situation: string;
    intention: string;
    options: Array<{
      label: string;
      wordChoice: string;
      line: string;
      effect: string;
      tradeoff: string;
    }>;
  };
  commonMisuse?: string | null;
  articulationDimensions?: Array<{
    option: string;
    changed: Record<string, string>;
  }>;
  enrichmentMisfire?: {
    line: string;
    whyTempting: string;
    whyWrong: string;
  };
  nextSteps?: Array<{
    target: string;
    kind: string;
    reason: string;
  }>;
  familyLinks?: Array<{
    word: string;
    reason: string;
  }>;
  learningConnections?: Array<{
    relation: string;
    targetId: string;
    stepKind: string | null;
    reason: string;
  }>;
  sourceReadingId: string;
  sourceReadingTitle: string;
  sourceParagraphId: string;
  sourceSentence: string;
  isFree: boolean;
};

type FoundationEntry = Pick<
  CommandVocabularyEntry,
  "handles" | "whyWorthHaving" | "distinction" | "combinations" | "examples" | "landingRange" | "commonMisuse"
> & Partial<Pick<
  CommandVocabularyEntry,
  "phonetic" | "partOfSpeech" | "meaning" | "alternateMeaning" | "synonym" | "antonym" | "family"
>>;

type TeachingLayer = {
  entries: Record<string, FoundationEntry>;
};

type Foundation = TeachingLayer & {
  schemaVersion: "command-vocabulary-foundation-v1";
  freeWords: string[];
};

type Release = {
  schemaVersion: "command-vocabulary-release-v1";
  freeWords: string[];
};
type Identities = {
  schemaVersion: "command-vocabulary-identities-v2";
  entryCount: 92;
  entries: Record<string, {
    lemma: string;
    sourceSenseId: string;
    partOfSpeech: string;
    readablePronunciation: string | null;
    pronunciationProvenance: {
      source: string;
      values: Array<{ value: string; variety?: string }>;
    } | null;
  }>;
};

type ReadablePronunciations = {
  schemaVersion: "verbalore-readable-pronunciations-v1";
  entries: Record<string, string>;
};

function pronunciationNeedsReplacement(value: string) {
  return !value.trim()
    || value.trim().startsWith("/")
    || /under review|not yet verified|pending|unavailable/iu.test(value);
}

const foundation = foundationData as Foundation;
const publishedSupplement = publishedSupplementData as TeachingLayer;
const release = releaseData as Release;
const identities = identityData as Identities;
const readablePronunciations = readablePronunciationsData as ReadablePronunciations;
const authoredEntries = {
  ...foundation.entries,
  ...publishedSupplement.entries,
};
const freeOrder = new Map(release.freeWords.map((word, index) => [word, index]));

function handlesForOutcomes(outcomes: ReadingOutcome[]) {
  const handles = new Set<CommandVocabularyHandle>();
  if (outcomes.some((outcome) => ["precise thought", "conceptual distillation", "lexical command"].includes(outcome))) {
    handles.add("think-precisely");
  }
  if (outcomes.some((outcome) => ["composed authority", "tactful firmness"].includes(outcome))) {
    handles.add("speak-with-confidence");
  }
  if (outcomes.some((outcome) => ["emotional precision", "inference"].includes(outcome))) {
    handles.add("understand-people");
  }
  if (outcomes.some((outcome) => ["cadence", "memorable expression", "wit"].includes(outcome))) {
    handles.add("write-memorably");
  }
  return [...handles].slice(0, 2).length
    ? [...handles].slice(0, 2)
    : ["think-precisely" as const];
}

function registersForExamples(examples: Array<{ context: string }>): CommandVocabularyRegister[] {
  const registers = new Set<CommandVocabularyRegister>();
  for (const { context } of examples) {
    if (context === "conversation") registers.add("conversational");
    if (context === "work") registers.add("professional");
    if (context === "writing") registers.add("literary");
    if (["thought", "argument", "public life"].includes(context)) registers.add("rhetorical");
  }
  return [...registers].slice(0, 2).length ? [...registers].slice(0, 2) : ["formal"];
}

function articulationsForLanding(
  landingRange: CommandVocabularyEntry["landingRange"],
): CommandVocabularyArticulation[] {
  const allowed = new Set<CommandVocabularyArticulation>([
    "direct",
    "warm-authority",
    "restrained",
    "tactful",
    "punchy",
  ]);
  return [...new Set((landingRange?.options || []).map(({ label }) =>
    label.toLocaleLowerCase().replaceAll(" ", "-") as CommandVocabularyArticulation
  ))].filter((label) => allowed.has(label));
}

function sourceFor(reading: Reading, word: string, insight: WordInsight) {
  const normalized = word.toLocaleLowerCase();
  const segment = reading.segments.find((candidate) => candidate.text === insight.sentence)
    || reading.segments.find((candidate) =>
      candidate.text.toLocaleLowerCase().split(/[^\p{L}'’-]+/u).includes(normalized)
    )
    || reading.segments[0];
  return {
    paragraphId: segment.id,
    sentence: segment.text,
  };
}

const unique = new Map<string, CommandVocabularyEntry>();

for (const reading of READINGS) {
  for (const [word, insight] of Object.entries(reading.words)) {
    if (unique.has(word)) continue;
    const authored = authoredEntries[word];
    const dictionaryIdentity = identities.entries[word];
    if (!dictionaryIdentity) throw new Error(`Command vocabulary is missing the central identity for ${word}.`);
    const source = sourceFor(reading, word, insight);
    const fallbackExamples = [
      { context: "example", sentence: insight.example },
      { context: `from ${reading.title}`, sentence: source.sentence },
    ].filter((example, index, all) =>
      all.findIndex((candidate) => candidate.sentence === example.sentence) === index
    );
    const examples = authored?.examples || fallbackExamples;
    const landingRange = authored?.landingRange;
    unique.set(word, {
      id: word,
      word,
      dictionaryIdentity: {
        lemma: dictionaryIdentity.lemma,
        sourceSenseId: dictionaryIdentity.sourceSenseId,
      },
      phonetic: authored?.phonetic
        || (pronunciationNeedsReplacement(insight.phonetic) && readablePronunciations.entries[word]
          ? readablePronunciations.entries[word]
          : insight.phonetic),
      pronunciationProvenance: dictionaryIdentity.pronunciationProvenance,
      partOfSpeech: authored?.partOfSpeech || insight.partOfSpeech,
      meaning: authored?.meaning || insight.meaning,
      alternateMeaning: authored?.alternateMeaning || insight.alternateMeaning,
      synonym: authored?.synonym || insight.synonym,
      antonym: authored?.antonym || insight.antonym,
      family: authored?.family || insight.family,
      whyWorthHaving: authored?.whyWorthHaving || insight.whyHere,
      handles: authored?.handles || handlesForOutcomes(reading.taxonomy.outcomes),
      registers: registersForExamples(examples),
      articulations: articulationsForLanding(landingRange),
      distinction: authored?.distinction,
      combinations: authored?.combinations || [],
      examples,
      landingRange,
      commonMisuse: authored?.commonMisuse,
      sourceReadingId: reading.id,
      sourceReadingTitle: reading.title,
      sourceParagraphId: source.paragraphId,
      sourceSentence: source.sentence,
      isFree: freeOrder.has(word),
    });
  }
}

export const COMMAND_VOCABULARY = [...unique.values()].sort((left, right) => {
  const leftFree = freeOrder.get(left.word);
  const rightFree = freeOrder.get(right.word);
  if (leftFree !== undefined && rightFree !== undefined) return leftFree - rightFree;
  if (leftFree !== undefined) return -1;
  if (rightFree !== undefined) return 1;
  return left.word.localeCompare(right.word);
});

export const COMMAND_VOCABULARY_BY_ID = new Map(
  COMMAND_VOCABULARY.map((entry) => [entry.id, entry]),
);

export const COMMAND_VOCABULARY_FREE_COUNT = release.freeWords.length;

if (COMMAND_VOCABULARY.length !== 92 || COMMAND_VOCABULARY_FREE_COUNT !== 50) {
  throw new Error("Command vocabulary must expose the 92 published words with an exact 50-word Free foundation.");
}

const missingAuthoredFreeWords = release.freeWords.filter((word) => !authoredEntries[word]);
if (missingAuthoredFreeWords.length) {
  throw new Error(`Every Free word needs a complete teaching layer: ${missingAuthoredFreeWords.join(", ")}`);
}
